import FormComponent from '../FormComponent';
import SelectorEngine from '../../utils/dom/selector-engine';
import EventHandler from '../../utils/dom/event-handler';
import SessionPlayersListItem from '../SessionPlayersListItem';
import Request, { ApiMode } from '../../utils/request';
import { startLoadingAnimation, stopLoadingAnimation } from '../../utils/dom/loading';
import { Events } from '../../utils/dom/html-events';

const IS_LOCAL = false;
const CLICK_EVENT = 'click';
export const EVENT_UPDATE_SESSION_DATA = `EVENT_UPDATE_SESSION_DATA`;

class SessionPlayersList extends FormComponent {
	set currentSession(currentSession = 0) {
		this.updateData({ currentSession });
		this.fetchSessionData();
	}

	get currentSession() {
		return this.data?.currentSession;
	}

	constructor(containerElement, config) {
		super(containerElement, config);
		this.data = {
			...this.data,
			totalSession: this.data?.supportingProps?.sessions?.length,
			loading: false
		};
		this.currentSession = 0;
		this.data?.supportingProps?.sessions
			?.filter((session) => !!session?.players?.length && !!session?.availableSeats)
			.map((session) => this.setupValueObject(session?.id, []));
	}

	reRenderQuestion(data) {
		const firstSessionWithError = data?.sessions?.[0];
		if (firstSessionWithError) {
			const sessionIndex = this.data?.supportingProps?.sessions.findIndex(
				(session) => session?.id === firstSessionWithError?.id
			);
			if (sessionIndex >= 0) {
				this.currentSession = sessionIndex;
			}
		}
	}

	markup(value, data = {}, titleMarkup) {
		this.sessionPlayerContainerClass = `lta-session-players-list-container-${data.id}`;
		const sessionData = data?.supportingProps?.sessions?.[data.currentSession];
		this.inputID = `lta-session-players-list-container-id-${data.id}`;
		return `<div class="lta-session-players-list-container">
					${sessionData?.availableSeats <= 0 ? this.renderFullBookedSnack(sessionData) : ''}
					${(sessionData?.players?.length <= 0 && !this.data.loading) ? this.renderNoPlayersSnack(sessionData) : ''}
					<div class="lta-session-players-list-container-title u-h1 u-mb-1">${data?.question}</div>
					${data?.instructionText ? `<p class="u-label2 u-mb-2 u-text-grey-ui-mid">${data?.instructionText}</p>` : ''}
					<div class="${this.sessionPlayerContainerClass}">
						<div class="u-caption2 u-mb-2">Session ${data.currentSession + 1} of ${data.totalSession}</div>
					</div>
					${
						data.currentSession < data.totalSession - 1
							? `<button type="button" class="lta-btn lta-btn--default lta-btn--primary lta-btn--block js-next-session-button">Next session</button>`
							: ``
					}
				</div>`;
	}

	renderFullBookedSnack(sessionData) {
		return `
			<div class="lta-snackbar lta-snackbar--hard-alert has-icon u-mb-2">
				<p class="lta-snackbar__label title title" aria-label="The ${sessionData.sessionName} session is now fully booked and has been removed from your booking" onclick="">
					The ${sessionData.sessionName} session is now fully booked and has been removed from your booking
				</p>
				<p class="text">
					<a href=${this.data?.supportingProps?.addSessionURL} class="u-cursor-pointer">Add another session</a> 
				</p> 
				<div class="lta-snackbar__icon">
					<svg class="lta-icon js-lta-text-input--btn-icon" width="1em" height="1em" style="color:currentColor;" fill="currentColor" alt="info" aria-hidden="true"><use xlink:href="/static/images/icons/lta-icons.svg#info"></use></svg>
				</div> 
			</div>`;
	}

	renderNoPlayersSnack(sessionData) {
		const title = `You've included the ${sessionData.sessionName} session, but unfortunately, there aren't any appropriate players for this session`;
		return `
		<div class="lta-snackbar lta-snackbar--hard-alert has-icon u-mb-2">
			<p class="lta-snackbar__label title title" aria-label="${title}">
				${title}
			</p>
			<p class="text">You can ${this.data.totalSession > 1 ? `either` : ''} <a class="u-cursor-pointer" id="p-add-player">add a new player</a>
				${this.data.totalSession > 1
					? `or <a class="u-cursor-pointer" id="p-remove-session">remove this session</a>`
					: ''
				}
			</p>
			<div class="lta-snackbar__icon">
				<svg class="lta-icon js-lta-text-input--btn-icon" width="1em" height="1em" style="color:currentColor;" fill="currentColor" alt="info" aria-hidden="true">
					<use xlink:href="/static/images/icons/lta-icons.svg#info"></use>
				</svg>
			</div>
		</div>`;
	}

	async afterRender() {
		this.sessionPlayerContainer = SelectorEngine.findOne(`.${this.sessionPlayerContainerClass}`, this.containerEl);
		this.sessionsData = this.data?.supportingProps?.sessions;
		if (this.sessionsData?.length > 0) {
			this.initEventListeners();
		}

		this.addPlayerSelector = SelectorEngine.findOne('#p-add-player', this.containerEl);
		this.removeSessionSelector = SelectorEngine.findOne('#p-remove-session', this.containerEl);

		EventHandler.on(this.addPlayerSelector, Events.Click, () => {
			this.currentSession = 0;
			this.data?.goBack();
		});

		EventHandler.on(this.removeSessionSelector, Events.Click, async () => {
			this.data.supportingProps.sessions = this.data.supportingProps.sessions.filter(
				(session, index) => index !== this.currentSession
			);
			this.data.totalSession = this.data.supportingProps.sessions.length;
			if (this.data.supportingProps.sessions.length > 0) {
				if (this.currentSession >= this.data.supportingProps.sessions.length) {
					this.currentSession = this.data.supportingProps.sessions.length - 1;
				}
				this.updateData(this.data);
				await this.fetchSessionData();
			} else {
				this.currentSession = Math.max(0, this.currentSession - 1);
				this.updateData(this.data);
				await this.fetchSessionData();
			}
			// NOTE: this statement is written to call the value change event
			this.value = this.value;
		});
	}

	performActionOnLoad() {
		this.currentSession = 0;
	}

	createSessionItemInstance() {
		const currentSessionData = this.sessionsData?.[this.data.currentSession];
		if (!this.sessionPlayerContainer || !currentSessionData) {
			return;
		}

		if (!!this.sessionObject) {
			this.sessionObject.remove();
		}

		this.sessionObject = new SessionPlayersListItem(this.sessionPlayerContainer, {
			data: {
				...currentSessionData
			}
		});

		this.sessionObject.onPlayerChange((sessionData) => {
			const selectedPlayersList = sessionData?.players?.filter((players) => players?.isChecked);
			this.setupValueObject(sessionData?.id, selectedPlayersList);
			EventHandler.trigger(SelectorEngine.findOne('body'), EVENT_UPDATE_SESSION_DATA, {
				sessions: this.data?.supportingProps?.sessions
			});
		});
	}

	initEventListeners() {
		this.nextSessionButton = SelectorEngine.findOne('.js-next-session-button', this.containerEl);
		if (this.nextSessionButton) {
			EventHandler.on(this.nextSessionButton, CLICK_EVENT, () => {
				this.nextSessionButtonClickHandler();
			});
		}
	}

	nextSessionButtonClickHandler() {
		const isValidSession = this.sessionObject.validate();
		if (this.data.currentSession < this.data.totalSession) {
			const sessionSupportingProps = this.data?.supportingProps;
			const sessionData = sessionSupportingProps?.sessions[this.data.currentSession];
			if (!!isValidSession) {
				this.currentSession = this.currentSession + 1;
				this.value = this.value;
			} else {
				this.sessionObject.data = {
					...this.sessionObject.data,
					hasError: sessionData?.availableSeats > 0
				};
			}
		}
	}

	async fetchSessionData() {
		const sessionSupportingProps = this.data?.supportingProps;
		this.sessionsData = sessionSupportingProps?.sessions;
		const sessionData = this.sessionsData?.[this.data.currentSession];
		if (!sessionData) return;

		const sessionDetails = sessionSupportingProps?.getSessionDetails;
		if (!sessionDetails?.endpoint) {
			this.createSessionItemInstance();
			return;
		}

		startLoadingAnimation(true);
		const { method, endpoint } = sessionDetails;
		try {
			const url = `${endpoint}&id=${sessionData?.id}`;
			this.updateData({ loading: true });
			const response = await Request?.[method?.toLowerCase() || 'get'](
				url,
				{},
				{
					local: IS_LOCAL,
					req: {
						apiMode: ApiMode.LtaError
					}
				}
			);
			const existingSessionIndex = this.data?.supportingProps?.sessions?.findIndex((data) => data?.id === response?.id);
			this.data.supportingProps.sessions[existingSessionIndex] = {
				...this.data.supportingProps.sessions[existingSessionIndex],
				...response
			};
			const _sessionData = this.data.supportingProps.sessions[existingSessionIndex];
			const _sessionId = _sessionData?.id;
			let _availableSeats = response.availableSeats;
			const selectedPlayers = this.value?.value?.[_sessionId];
			if (selectedPlayers) {
				_sessionData?.players?.forEach((player) => {
					const _isChecked = selectedPlayers?.some((_player) => player.id === _player.id);
					// const _isChecked = true;
					if (_availableSeats > 0 && _isChecked) {
						player.isChecked = _isChecked;
						_availableSeats = _availableSeats - 1;
					} else {
						player.isChecked = false;
					}
				});
			}
			this.updateData({
				...this.data,
				loading: false
			});
			EventHandler.trigger(SelectorEngine.findOne('body'), EVENT_UPDATE_SESSION_DATA, {
				sessions: this.data?.supportingProps?.sessions
			});
			if (!!error?.hasError) {
				throw error;
			}
		} catch (error) {
			if (error.hasError) {
				// eslint-disable-next-line no-console
				console.error('Error :', error);
			}
		} finally {
			stopLoadingAnimation();
			this.createSessionItemInstance();
		}
	}

	setupValueObject(key, value) {
		this.value = {
			...this.data,
			value: value
				? {
						...(this.value?.value || {}),
						[key]: value
				  }
				: undefined
		};

		if (this.value?.dependentQuestion) {
			this.dependentQuestion = this.value.dependentQuestion;
		}
	}

	validate() {
		const currentSessionData =
			this.data?.supportingProps?.sessions?.[this.data.currentSession];

		if (!currentSessionData) return true;

		// Checking if at least one player selected for CURRENT session
		const selectedPlayers = this.value?.value?.[currentSessionData.id];

		const isValid =
			(Array.isArray(selectedPlayers) && selectedPlayers.length > 0) ||
			currentSessionData.availableSeats === 0;

		if (!isValid && this.sessionObject) {
			this.sessionObject.data = {
				...this.sessionObject.data,
				hasError: true
			};
		}

		return isValid;
	}

	shouldShowNext() {
		return this.data.totalSession - (this.data.currentSession + 1) < 1;
	}
}

export default SessionPlayersList;
